import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { 
  Search, 
  Plus, 
  Upload, 
  MoreVertical, 
  ArrowLeft,
  Edit,
  Filter,
  Eye,
  Target,
  Building2,
  MapPin,
  Pause,
  Play,
  FileText,
  CheckCircle,
  AlertCircle,
  Loader2,
  IndianRupee,
  Users,
  Trash2,
  Phone,
  Mail,
  User,
  Ruler
} from 'lucide-react';
import { toast } from 'sonner';

const CITIES = ["Mumbai", "Bangalore", "Delhi", "Chennai", "Hyderabad", "Pune", "Kolkata", "Ahmedabad"];
const STATUSES = ["Active", "Paused"];
const SPOC_TYPES = ["sales", "crm", "support", "finance", "site", "channel", "marketing", "other"];

const AdminProjects = ({ user }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    project_name: '',
    city: '',
    location: '',
    builder_name: '',
    possession: '',
    status: 'Active',
    rera_id: ''
  });
  
  // Config Pricing state
  const [configPricing, setConfigPricing] = useState([]);
  
  // SPOCs state
  const [spocs, setSpocs] = useState([]);
  
  // Upload state
  const [uploadTab, setUploadTab] = useState('master');
  const [csvContent, setCsvContent] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [templates, setTemplates] = useState(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (cityFilter) params.append('city', cityFilter);
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '200');

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects?${params.toString()}`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          credentials: 'include'
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch projects');
      
      const data = await response.json();
      setProjects(data.projects || []);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [search, cityFilter, statusFilter]);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/upload-templates`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          credentials: 'include'
        }
      );
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  const fetchProjectDetails = async (projectId) => {
    try {
      const [pricingRes, spocsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/pricing`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          credentials: 'include'
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/spocs`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          credentials: 'include'
        })
      ]);
      
      if (pricingRes.ok) {
        const pricingData = await pricingRes.json();
        setConfigPricing(pricingData.pricing || []);
      }
      
      if (spocsRes.ok) {
        const spocsData = await spocsRes.json();
        setSpocs(spocsData.spocs || []);
      }
    } catch (error) {
      console.error('Failed to fetch project details');
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Add new config pricing row
  const addConfigPricing = () => {
    setConfigPricing([...configPricing, {
      configuration: '',
      min_price_lakhs: '',
      max_price_lakhs: '',
      carpet_area_sqft: '',
      super_area_sqft: '',
      notes: ''
    }]);
  };

  // Remove config pricing row
  const removeConfigPricing = (index) => {
    setConfigPricing(configPricing.filter((_, i) => i !== index));
  };

  // Update config pricing row
  const updateConfigPricing = (index, field, value) => {
    const updated = [...configPricing];
    updated[index] = { ...updated[index], [field]: value };
    setConfigPricing(updated);
  };

  // Add new SPOC row
  const addSpoc = () => {
    setSpocs([...spocs, {
      spoc_name: '',
      spoc_email: '',
      spoc_phone: '',
      spoc_type: 'sales',
      designation: '',
      is_primary: false
    }]);
  };

  // Remove SPOC row
  const removeSpoc = (index) => {
    setSpocs(spocs.filter((_, i) => i !== index));
  };

  // Update SPOC row
  const updateSpoc = (index, field, value) => {
    const updated = [...spocs];
    updated[index] = { ...updated[index], [field]: value };
    setSpocs(updated);
  };

  const handleAddProject = async () => {
    if (!formData.project_name || !formData.city) {
      toast.error('Please fill project name and city');
      return;
    }

    setSaving(true);
    try {
      // Create project
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.detail || 'Failed to create project');
        return;
      }

      const newProject = await response.json();
      const projectId = newProject.project_id;

      // Save config pricing if any
      if (configPricing.length > 0) {
        const validPricing = configPricing.filter(p => p.configuration);
        for (const pricing of validPricing) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/${projectId}/pricing`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            credentials: 'include',
            body: JSON.stringify(pricing)
          });
        }
      }

      // Save SPOCs if any
      if (spocs.length > 0) {
        const validSpocs = spocs.filter(s => s.spoc_name);
        for (const spoc of validSpocs) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/${projectId}/spocs`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            credentials: 'include',
            body: JSON.stringify(spoc)
          });
        }
      }

      toast.success('Project created successfully!');
      setShowAddDialog(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error('Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  const handleEditProject = async () => {
    if (!selectedProject) return;

    setSaving(true);
    try {
      // Update project
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${selectedProject.project_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          credentials: 'include',
          body: JSON.stringify(formData)
        }
      );

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update project');
        return;
      }

      const projectId = selectedProject.project_id;

      // Update config pricing - delete existing and re-add
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/${projectId}/pricing`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        credentials: 'include'
      });

      if (configPricing.length > 0) {
        const validPricing = configPricing.filter(p => p.configuration);
        for (const pricing of validPricing) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/${projectId}/pricing`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            credentials: 'include',
            body: JSON.stringify(pricing)
          });
        }
      }

      // Update SPOCs - delete existing and re-add
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/${projectId}/spocs`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        credentials: 'include'
      });

      if (spocs.length > 0) {
        const validSpocs = spocs.filter(s => s.spoc_name);
        for (const spoc of validSpocs) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/projects/${projectId}/spocs`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            credentials: 'include',
            body: JSON.stringify(spoc)
          });
        }
      }

      toast.success('Project updated successfully');
      setShowEditDialog(false);
      setSelectedProject(null);
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error('Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (project) => {
    const newStatus = project.status === 'Active' ? 'Paused' : 'Active';
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${project.project_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          credentials: 'include',
          body: JSON.stringify({ status: newStatus })
        }
      );

      if (!response.ok) {
        toast.error('Failed to update status');
        return;
      }

      toast.success(`Project ${newStatus.toLowerCase()}`);
      fetchProjects();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpload = async () => {
    if (!csvContent.trim()) {
      toast.error('Please upload or paste CSV content');
      return;
    }

    setUploadLoading(true);
    setUploadResults(null);

    const endpoints = {
      master: '/api/admin/projects/upload-master',
      pricing: '/api/admin/projects/upload-config-pricing',
      spocs: '/api/admin/projects/upload-spocs'
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}${endpoints[uploadTab]}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          credentials: 'include',
          body: JSON.stringify({ csv_content: csvContent })
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Upload failed');
        return;
      }

      setUploadResults(data);
      
      const successCount = data.created || data.inserted || 0;
      const updateCount = data.updated || 0;
      
      if (successCount > 0 || updateCount > 0) {
        toast.success(`Success: ${successCount} ${uploadTab === 'master' ? 'created' : 'inserted'}, ${updateCount} updated`);
        if (uploadTab === 'master') {
          fetchProjects();
        }
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvContent(event.target.result);
      toast.success(`File loaded: ${file.name}`);
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsText(file);
  };

  const resetUpload = () => {
    setCsvContent('');
    setUploadResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFormData({
      project_name: '',
      city: '',
      location: '',
      builder_name: '',
      possession: '',
      status: 'Active',
      rera_id: ''
    });
    setConfigPricing([]);
    setSpocs([]);
  };

  const openEditDialog = async (project) => {
    setSelectedProject(project);
    setFormData({
      project_name: project.project_name || '',
      city: project.city || '',
      location: project.location || '',
      builder_name: project.builder_name || '',
      possession: project.possession || '',
      status: project.status || 'Active',
      rera_id: project.rera_id || ''
    });
    
    // Fetch existing pricing and SPOCs
    await fetchProjectDetails(project.project_id);
    
    setShowEditDialog(true);
  };

  const openAddDialog = () => {
    resetForm();
    setShowAddDialog(true);
  };

  const openUploadDialog = () => {
    resetUpload();
    fetchTemplates();
    setShowUploadDialog(true);
  };

  const clearFilters = () => {
    setCityFilter('');
    setStatusFilter('');
    setSearch('');
  };

  const formatPrice = (min, max) => {
    const formatValue = (lakh) => {
      if (!lakh) return null;
      if (lakh >= 100) return `${(lakh/100).toFixed(1)}Cr`;
      return `${lakh}L`;
    };
    
    const minStr = formatValue(min);
    const maxStr = formatValue(max);
    
    if (minStr && maxStr) return `₹${minStr} - ${maxStr}`;
    if (minStr) return `₹${minStr}+`;
    if (maxStr) return `Up to ₹${maxStr}`;
    return null;
  };

  const hasActiveFilters = cityFilter || statusFilter;

  const uploadTabConfig = {
    master: {
      label: 'Projects Master',
      icon: Building2,
      color: 'bg-blue-500',
      description: 'Upload project basic information. Unique key: (project_name + city)'
    },
    pricing: {
      label: 'Config Pricing',
      icon: IndianRupee,
      color: 'bg-emerald-500',
      description: 'Upload configuration-wise pricing. Must match existing projects.'
    },
    spocs: {
      label: 'SPOCs',
      icon: Users,
      color: 'bg-purple-500',
      description: 'Upload project contacts. Must match existing projects.'
    }
  };

  // Project Form Component (used in both Add and Edit)
  const ProjectForm = ({ isEdit = false }) => (
    <Tabs defaultValue="basic" className="w-full">
      <TabsList className="grid grid-cols-3 mb-4">
        <TabsTrigger value="basic" className="text-xs">Basic Info</TabsTrigger>
        <TabsTrigger value="pricing" className="text-xs">Config Pricing</TabsTrigger>
        <TabsTrigger value="spocs" className="text-xs">SPOCs</TabsTrigger>
      </TabsList>

      {/* Basic Info Tab */}
      <TabsContent value="basic" className="space-y-4">
        <div className="space-y-2">
          <Label>Project Name *</Label>
          <Input
            value={formData.project_name}
            onChange={(e) => setFormData({...formData, project_name: e.target.value})}
            placeholder="Enter project name"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>City *</Label>
            <Select value={formData.city} onValueChange={(v) => setFormData({...formData, city: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Select city" />
              </SelectTrigger>
              <SelectContent>
                {CITIES.map(city => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(status => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Location / Area</Label>
          <Input
            value={formData.location}
            onChange={(e) => setFormData({...formData, location: e.target.value})}
            placeholder="e.g., Whitefield, Bandra West"
          />
        </div>
        <div className="space-y-2">
          <Label>Builder Name</Label>
          <Input
            value={formData.builder_name}
            onChange={(e) => setFormData({...formData, builder_name: e.target.value})}
            placeholder="e.g., Prestige Group"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Possession</Label>
            <Input
              value={formData.possession}
              onChange={(e) => setFormData({...formData, possession: e.target.value})}
              placeholder="e.g., Dec 2025"
            />
          </div>
          <div className="space-y-2">
            <Label>RERA ID</Label>
            <Input
              value={formData.rera_id}
              onChange={(e) => setFormData({...formData, rera_id: e.target.value})}
              placeholder="e.g., PRM/KA/RERA/123"
            />
          </div>
        </div>
      </TabsContent>

      {/* Config Pricing Tab */}
      <TabsContent value="pricing" className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Add configuration-wise pricing</p>
          <Button type="button" variant="outline" size="sm" onClick={addConfigPricing}>
            <Plus size={14} className="mr-1" /> Add Config
          </Button>
        </div>
        
        {configPricing.length === 0 ? (
          <div className="text-center py-6 text-slate-400 bg-slate-50 rounded-lg">
            <IndianRupee size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No pricing configurations added</p>
            <Button type="button" variant="link" size="sm" onClick={addConfigPricing} className="mt-2">
              + Add first configuration
            </Button>
          </div>
        ) : (
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {configPricing.map((config, index) => (
              <div key={index} className="p-3 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">Configuration {index + 1}</span>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeConfigPricing(index)}
                    className="h-6 w-6 p-0 text-[#5B3DF5] hover:text-[#5B3DF5]"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Config Name *</Label>
                    <Input
                      value={config.configuration}
                      onChange={(e) => updateConfigPricing(index, 'configuration', e.target.value)}
                      placeholder="e.g., 2 BHK"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Min Price (L)</Label>
                    <Input
                      type="number"
                      value={config.min_price_lakhs}
                      onChange={(e) => updateConfigPricing(index, 'min_price_lakhs', e.target.value)}
                      placeholder="e.g., 85"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Max Price (L)</Label>
                    <Input
                      type="number"
                      value={config.max_price_lakhs}
                      onChange={(e) => updateConfigPricing(index, 'max_price_lakhs', e.target.value)}
                      placeholder="e.g., 95"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Carpet Area (sqft)</Label>
                    <Input
                      type="number"
                      value={config.carpet_area_sqft}
                      onChange={(e) => updateConfigPricing(index, 'carpet_area_sqft', e.target.value)}
                      placeholder="e.g., 1050"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Super Area (sqft)</Label>
                    <Input
                      type="number"
                      value={config.super_area_sqft}
                      onChange={(e) => updateConfigPricing(index, 'super_area_sqft', e.target.value)}
                      placeholder="e.g., 1250"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Notes</Label>
                    <Input
                      value={config.notes}
                      onChange={(e) => updateConfigPricing(index, 'notes', e.target.value)}
                      placeholder="Optional"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </TabsContent>

      {/* SPOCs Tab */}
      <TabsContent value="spocs" className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Add project contacts (SPOCs)</p>
          <Button type="button" variant="outline" size="sm" onClick={addSpoc}>
            <Plus size={14} className="mr-1" /> Add SPOC
          </Button>
        </div>
        
        {spocs.length === 0 ? (
          <div className="text-center py-6 text-slate-400 bg-slate-50 rounded-lg">
            <User size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No SPOCs added</p>
            <Button type="button" variant="link" size="sm" onClick={addSpoc} className="mt-2">
              + Add first SPOC
            </Button>
          </div>
        ) : (
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {spocs.map((spoc, index) => (
              <div key={index} className="p-3 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">SPOC {index + 1}</span>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeSpoc(index)}
                    className="h-6 w-6 p-0 text-[#5B3DF5] hover:text-[#5B3DF5]"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Name *</Label>
                    <Input
                      value={spoc.spoc_name}
                      onChange={(e) => updateSpoc(index, 'spoc_name', e.target.value)}
                      placeholder="Contact name"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select 
                      value={spoc.spoc_type} 
                      onValueChange={(v) => updateSpoc(index, 'spoc_type', v)}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SPOC_TYPES.map(type => (
                          <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input
                      value={spoc.spoc_phone}
                      onChange={(e) => updateSpoc(index, 'spoc_phone', e.target.value)}
                      placeholder="9876543210"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      value={spoc.spoc_email}
                      onChange={(e) => updateSpoc(index, 'spoc_email', e.target.value)}
                      placeholder="email@example.com"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Designation</Label>
                    <Input
                      value={spoc.designation}
                      onChange={(e) => updateSpoc(index, 'designation', e.target.value)}
                      placeholder="e.g., Sales Manager"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <Switch
                      checked={spoc.is_primary}
                      onCheckedChange={(checked) => updateSpoc(index, 'is_primary', checked)}
                    />
                    <Label className="text-xs">Primary Contact</Label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-800">Project Management</h1>
            <p className="text-xs text-slate-500">{projects.length} projects</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Search & Filters */}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search project, location, builder..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 rounded-xl"
            />
          </div>
          <Button
            variant={showFilters ? "default" : "outline"}
            size="icon"
            className="h-11 w-11 rounded-xl"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
          </Button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <Card className="p-4 bg-white border-0 shadow-sm rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Filters</span>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-xs text-[#5B3DF5] hover:text-[#5B3DF5]">
                  Clear all
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={cityFilter || "all"} onValueChange={(v) => setCityFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-lg text-sm">
                  <SelectValue placeholder="City" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {CITIES.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-lg text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUSES.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={openAddDialog}
            className="flex-1 h-11 bg-[#5B3DF5] hover:bg-[#4A2FE0] rounded-xl"
          >
            <Plus size={18} className="mr-2" />
            Add Project
          </Button>
          <Button 
            variant="outline"
            onClick={openUploadDialog}
            className="h-11 rounded-xl"
            data-testid="bulk-upload-btn"
          >
            <Upload size={18} className="mr-2" />
            CSV Upload
          </Button>
        </div>

        {/* Upload Order Info */}
        <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700">
          <p className="font-medium mb-1">📋 CSV Upload Order:</p>
          <p>1. Projects Master → 2. Config Pricing → 3. SPOCs</p>
        </div>

        {/* Projects List */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <Card className="p-8 text-center bg-white border-0 shadow-sm rounded-xl">
            <Building2 size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No projects found</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {projects.map(project => {
              const priceRange = formatPrice(
                project.price_range_min || project.price_min_lakh,
                project.price_range_max || project.price_max_lakh
              );
              
              return (
                <Card 
                  key={project.project_id} 
                  className="p-3 bg-white border-0 shadow-sm rounded-xl"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-800 truncate">
                          {project.project_name}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          project.status === 'Active' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {project.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <MapPin size={12} className="text-slate-400" />
                        <span className="text-sm text-slate-500">
                          {project.location ? `${project.location}, ` : ''}{project.city}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        {priceRange && (
                          <span className="text-sm font-medium text-[#5B3DF5] flex items-center gap-1">
                            <IndianRupee size={12} />
                            {priceRange.replace('₹', '')}
                          </span>
                        )}
                        <div className="flex items-center gap-3 text-xs text-slate-400">
                          <span className="flex items-center gap-1" title="Visits (FYTD)">
                            <Eye size={14} /> {project.visited_count || 0}
                          </span>
                          <span className={`flex items-center gap-1 ${
                            (project.booked_count || 0) > 0 ? 'text-emerald-600' : ''
                          }`} title="Bookings (FYTD)">
                            <Target size={14} /> {project.booked_count || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(project)}>
                          <Edit size={14} className="mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleStatus(project)}>
                          {project.status === 'Active' ? (
                            <>
                              <Pause size={14} className="mr-2" />
                              Pause
                            </>
                          ) : (
                            <>
                              <Play size={14} className="mr-2" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Project Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Project</DialogTitle>
          </DialogHeader>
          <div className="pt-4">
            <ProjectForm isEdit={false} />
            <Button 
              onClick={handleAddProject} 
              className="w-full mt-6 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Project'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="pt-4">
            <ProjectForm isEdit={true} />
            <Button 
              onClick={handleEditProject} 
              className="w-full mt-6 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => {
        setShowUploadDialog(open);
        if (!open) resetUpload();
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={20} />
              Project Data Upload
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pt-2">
            {/* Upload Type Tabs */}
            <Tabs value={uploadTab} onValueChange={(v) => { setUploadTab(v); resetUpload(); }}>
              <TabsList className="grid grid-cols-3 h-auto p-1 bg-slate-100">
                {Object.entries(uploadTabConfig).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <TabsTrigger 
                      key={key} 
                      value={key}
                      className="flex flex-col gap-1 py-2 data-[state=active]:bg-white"
                    >
                      <Icon size={16} />
                      <span className="text-xs">{config.label}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {Object.entries(uploadTabConfig).map(([key, config]) => (
                <TabsContent key={key} value={key} className="mt-4 space-y-4">
                  {/* Instructions */}
                  <div className={`p-3 rounded-lg text-sm text-white ${config.color}`}>
                    <p className="font-medium">{config.label}</p>
                    <p className="text-xs opacity-90 mt-1">{config.description}</p>
                  </div>

                  {/* Template Info */}
                  {templates && templates[key === 'master' ? 'projects_master' : key === 'pricing' ? 'config_pricing' : 'project_spocs'] && (
                    <div className="p-3 bg-slate-50 rounded-lg text-xs space-y-2">
                      <p className="font-medium text-slate-700">CSV Columns:</p>
                      <code className="block bg-white p-2 rounded text-slate-600 overflow-x-auto">
                        {templates[key === 'master' ? 'projects_master' : key === 'pricing' ? 'config_pricing' : 'project_spocs'].columns}
                      </code>
                      <p className="text-slate-500">
                        <span className="font-medium">Example:</span>{' '}
                        {templates[key === 'master' ? 'projects_master' : key === 'pricing' ? 'config_pricing' : 'project_spocs'].example}
                      </p>
                    </div>
                  )}

                  {/* File Upload */}
                  <div 
                    className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-slate-300 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <FileText size={32} className="mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-600">Click to select CSV file</p>
                  </div>

                  {/* Or paste */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-2 text-slate-500">or paste CSV</span>
                    </div>
                  </div>

                  <textarea
                    value={csvContent}
                    onChange={(e) => setCsvContent(e.target.value)}
                    placeholder="Paste CSV content here..."
                    className="w-full h-28 p-3 border rounded-lg text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-[#EF4444]"
                  />

                  {csvContent && (
                    <div className="flex items-center gap-2 text-sm text-emerald-600">
                      <CheckCircle size={16} />
                      CSV loaded ({csvContent.split('\n').length} lines)
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>

            {/* Upload Results */}
            {uploadResults && (
              <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-sm text-slate-700">Upload Summary</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-emerald-50 rounded">
                    <p className="text-lg font-bold text-emerald-600">
                      {uploadResults.created || uploadResults.inserted || 0}
                    </p>
                    <p className="text-xs text-emerald-700">
                      {uploadTab === 'master' ? 'Created' : 'Inserted'}
                    </p>
                  </div>
                  {uploadTab === 'master' && (
                    <div className="p-2 bg-blue-50 rounded">
                      <p className="text-lg font-bold text-blue-600">{uploadResults.updated || 0}</p>
                      <p className="text-xs text-blue-700">Updated</p>
                    </div>
                  )}
                  <div className="p-2 bg-red-50 rounded">
                    <p className="text-lg font-bold text-[#5B3DF5]">{uploadResults.failed || 0}</p>
                    <p className="text-xs text-red-700">Failed</p>
                  </div>
                </div>
                
                {uploadResults.errors && uploadResults.errors.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto bg-red-50 p-2 rounded">
                    <p className="text-xs font-medium text-[#5B3DF5] mb-1">Errors:</p>
                    {uploadResults.errors.slice(0, 10).map((err, i) => (
                      <p key={i} className="text-xs text-[#5B3DF5]">{err}</p>
                    ))}
                    {uploadResults.errors.length > 10 && (
                      <p className="text-xs text-slate-500">...and {uploadResults.errors.length - 10} more</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4 border-t mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowUploadDialog(false)}
              className="flex-1"
            >
              {uploadResults ? 'Done' : 'Cancel'}
            </Button>
            {!uploadResults && (
              <Button 
                onClick={handleUpload}
                disabled={!csvContent.trim() || uploadLoading}
                className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
              >
                {uploadLoading ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} className="mr-2" />
                    Upload {uploadTabConfig[uploadTab].label}
                  </>
                )}
              </Button>
            )}
            {uploadResults && (
              <Button 
                onClick={resetUpload}
                className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
              >
                Upload More
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProjects;
